// src/hooks/useSiteLikes.ts
// Hook interaksi Likes (Dukungan) yang melekat per-device menggunakan persistent Device ID & Supabase

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const DEVICE_ID_KEY = 'evano_device_id_v1';
const LIKED_KEY = 'evano_device_has_liked_v1';
const LIKES_COUNT_KEY = 'evano_total_likes_v1';
const BASELINE_LIKES = 128; // Nilai awal dasar portofolio Vano

export const getOrCreateDeviceId = (): string => {
  if (typeof window === 'undefined') return 'server_id';
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = `dev_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return 'temp_device_id';
  }
};

export const useSiteLikes = () => {
  const [likesCount, setLikesCount] = useState<number>(() => {
    if (typeof window === 'undefined') return BASELINE_LIKES;
    const cached = localStorage.getItem(LIKES_COUNT_KEY);
    return cached ? parseInt(cached, 10) : BASELINE_LIKES;
  });

  const [isLiked, setIsLiked] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(LIKED_KEY) === 'true';
  });

  const [isLoading, setIsLoading] = useState(false);

  // Sync like count & status device dari Supabase
  const fetchLikesData = useCallback(async () => {
    const deviceId = getOrCreateDeviceId();

    try {
      // 1. Cek apakah device ini sudah tercatat di tabel `site_likes`
      const { data: deviceLike, error: deviceError } = await supabase
        .from('site_likes')
        .select('id')
        .eq('device_id', deviceId)
        .maybeSingle();

      if (!deviceError) {
        const hasLiked = !!deviceLike;
        setIsLiked(hasLiked);
        localStorage.setItem(LIKED_KEY, hasLiked ? 'true' : 'false');
      }

      // 2. Ambil total like dari tabel `site_likes` di Supabase
      const { count: totalTableLikes, error: countError } = await supabase
        .from('site_likes')
        .select('*', { count: 'exact', head: true });

      if (!countError && totalTableLikes !== null) {
        // Akumulasi: Baseline 128 + jumlah total likes di database dari semua device
        const finalCount = BASELINE_LIKES + totalTableLikes;
        setLikesCount(finalCount);
        localStorage.setItem(LIKES_COUNT_KEY, finalCount.toString());
        return;
      }
    } catch {
      // Fallback local cache
    }
  }, []);

  useEffect(() => {
    fetchLikesData();

    // Dengarkan event sinkronisasi antar komponen di tab/halaman yang sama
    const handleSync = (e: any) => {
      if (e?.detail) {
        if (typeof e.detail.likesCount === 'number') setLikesCount(e.detail.likesCount);
        if (typeof e.detail.isLiked === 'boolean') setIsLiked(e.detail.isLiked);
      }
    };

    window.addEventListener('evano_like_sync', handleSync);
    return () => {
      window.removeEventListener('evano_like_sync', handleSync);
    };
  }, [fetchLikesData]);

  // Toggle Like (Like / Unlike)
  const toggleLike = async () => {
    if (isLoading) return;
    setIsLoading(true);

    const deviceId = getOrCreateDeviceId();
    const willBeLiked = !isLiked;
    const optimisticCount = willBeLiked ? likesCount + 1 : Math.max(BASELINE_LIKES, likesCount - 1);

    // Optimistic UI Update seketika
    setIsLiked(willBeLiked);
    setLikesCount(optimisticCount);
    localStorage.setItem(LIKED_KEY, willBeLiked ? 'true' : 'false');
    localStorage.setItem(LIKES_COUNT_KEY, optimisticCount.toString());

    window.dispatchEvent(
      new CustomEvent('evano_like_sync', {
        detail: { isLiked: willBeLiked, likesCount: optimisticCount },
      })
    );

    try {
      // 1. Simpan perubahan ke Supabase
      if (willBeLiked) {
        await supabase
          .from('site_likes')
          .upsert([{ device_id: deviceId }], { onConflict: 'device_id' });
      } else {
        await supabase
          .from('site_likes')
          .delete()
          .eq('device_id', deviceId);
      }

      // 2. Query ulang total like yang akurat dari Supabase
      const { count: freshCount, error: freshErr } = await supabase
        .from('site_likes')
        .select('*', { count: 'exact', head: true });

      if (!freshErr && freshCount !== null) {
        const accurateCount = BASELINE_LIKES + freshCount;
        setLikesCount(accurateCount);
        localStorage.setItem(LIKES_COUNT_KEY, accurateCount.toString());

        window.dispatchEvent(
          new CustomEvent('evano_like_sync', {
            detail: { isLiked: willBeLiked, likesCount: accurateCount },
          })
        );
      }
    } catch {
      // Jika tabel belum ada di Supabase, optimistic update tetap berjalan di lokal
    } finally {
      setIsLoading(false);
    }
  };

  return {
    likesCount,
    isLiked,
    toggleLike,
    isLoading,
  };
};
